import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { Button } from 'ui-components/components/button';

import { addCustomPackage } from '../../../../settings/packages-settings/api';
import { CUSTOM_PACKAGE_DEFAULTS } from '../../../../settings/packages-settings/constants';
import CustomPackagesModal from '../../../../settings/packages-settings/custom-packages/custom-packages-modal';
import Global from '../../../../../store/global-selectors';
import { MODALS } from '../../../../settings/packages-settings/custom-packages';
import { notifications } from '../../../../settings/components/notifications';
import { PackageService } from '../../../../../services/package-service';
import { refreshPackagesSuccess } from '../../../../../store/seller/seller.actions';
import { useModal } from '../../../../../hooks/use-modal';

import { messages } from '../messages';
import { useTrackWorkflow, WorkflowSteps } from '../../../../../tracking/use-workflow-tracking';
import { EventSourceStrings, Workflows } from '../../../../../tracking/tracking-constants';

export const CreateCustomPackageButton = ({ onActionClicked }) => {
  const dispatch = useDispatch();
  const sellerDefaultDimensionUnit = useSelector(Global.selectSellerDefaultDimensionUnit);
  const packages = useSelector(Global.selectSellerPackages);
  const trackCustomPackageFlow = useTrackWorkflow(Workflows.CUSTOM_PACKAGE_FLOW);

  const { open, close, isOpen } = useModal();

  const defaultPackageConfig = {
    dimensions: {
      length: 0,
      width: 0,
      height: 0,
      unit: sellerDefaultDimensionUnit,
    },
    name: '',
  };

  const handleSave = useCallback(
    (formValues) => {
      const customPackage = Object.entries(CUSTOM_PACKAGE_DEFAULTS).reduce(
        (result, [key, defaultValue]) => ({
          ...result,
          [key]: formValues[key] || defaultValue,
        }),
        {},
      );

      const addPackage = () => addCustomPackage(customPackage);

      const fetchData = async () => {
        try {
          const updatedPackage = (await addPackage()) || customPackage;
          updatedPackage.packageId = PackageService.buildCompositePackageId(updatedPackage);
          dispatch(refreshPackagesSuccess([...packages, updatedPackage]));
          notifications.success();
          trackCustomPackageFlow(WorkflowSteps.COMPLETE, { updatedPackage });
        } catch (e) {
          notifications.error(e);
          trackCustomPackageFlow(WorkflowSteps.ERROR, { error: e });
        } finally {
          close();
        }
      };
      fetchData();
    },
    [dispatch, packages, trackCustomPackageFlow, close],
  );

  const openAndCompleteCard = useCallback(() => {
    open();
    onActionClicked();
    trackCustomPackageFlow(WorkflowSteps.START, {
      source: EventSourceStrings.HOME_PAGE_FEED,
    });
  }, [open, onActionClicked, trackCustomPackageFlow]);

  return (
    <div>
      <Button onClick={openAndCompleteCard} theme='tertiary'>
        <FormattedMessage {...messages.addPackage} />
      </Button>
      {isOpen && (
        <CustomPackagesModal
          action={MODALS.add}
          onClose={close}
          onSave={handleSave}
          packageInfo={defaultPackageConfig}
          unit={sellerDefaultDimensionUnit}
        />
      )}
    </div>
  );
};

CreateCustomPackageButton.propTypes = {
  onActionClicked: PropTypes.func.isRequired,
};
